import React, { useMemo, useState } from "react";
import moment from "moment";
import { Box, Typography } from "@mui/material";
import {
  getEffectiveFormQuestions,
  isMeasurableQuestion,
} from "./public/publicJoinStorage";
import { submitMemberTrackerApi } from "src/DAL/tracker/tracker";
import { usePublicFormState } from "./public/PublicTrackerField";
import { getJourneySteps } from "./memberTrackersStorage";
import { isJourneyPasswordRequired } from "./JourneyPasswordGate";
import {
  buildIdentitySeedAnswers,
  filterMemberVisibleQuestions,
  filterPublicQuestions,
  getLoggedInMemberPrefill,
} from "./public/publicTrackerFlow";
import {
  answerOnRow,
  GeneralQuestion,
  MeasureQuestion,
} from "./MemberCheckInFields";
import { gr } from "src/design-system/tokens";
import GrCard from "src/design-system/components/GrCard";
import GrCardHeader from "src/design-system/components/GrCardHeader";
import GrPageHeader from "src/design-system/components/GrPageHeader";
import GrBadge from "src/design-system/components/GrBadge";
import GrButton from "src/design-system/components/GrButton";
import GrText from "src/design-system/components/GrText";

const MUTED = "#64748b";
const INK = "#0f172a";

const formSx = { width: "100%", display: "grid", gap: gr.space[6] };
// 950: the question card stays readable on wide screens (layout width, not a token).
const bodyCardSx = {
  width: "100%",
  maxWidth: 950,
  mx: "auto",
  display: "flex",
  flexDirection: "column",
  gap: gr.space[6],
};
const dividerSx = { borderTop: `1px solid ${gr.color.line2}` };
const answeredWideSx = { display: { xs: "none", sm: "inline-flex" } };
const answeredPhoneSx = { display: { xs: "inline-flex", sm: "none" }, mt: gr.space[3] };
const actionsSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: gr.space[3],
};

/** Section title with its question count. */
const sectionTitle = (text, count) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: gr.space[2] }}>
    {text}
    <GrBadge>{count}</GrBadge>
  </Box>
);

/**
 * Portal check-in — header (date, status, answered count), then General
 * questions and Measurements (last time + better than last time).
 * lateStep: a missed check-in (schedule step) sent late instead of the open one.
 */
export default function MemberTrackerForm({
  tracker,
  onCancel,
  onSuccess,
  lateStep = null,
}) {
  const memberInfo = useMemo(() => getLoggedInMemberPrefill(), []);

  const memberSubs = useMemo(
    () =>
      (tracker?._memberSubmissions || []).map((row) => ({
        ...row,
        id: String(row._id || row.id),
        formId: row.formId,
        submittedAt: row.submittedAt,
      })),
    [tracker]
  );

  const journeySteps = useMemo(
    () => getJourneySteps(tracker, memberSubs),
    [tracker, memberSubs]
  );

  const form = useMemo(() => {
    const current =
      lateStep || journeySteps.find((s) => s.state === "current");
    // Schedule steps carry formId (one check-in form, many dates).
    const currentFormId = current?.formId || current?.id;
    if (currentFormId) {
      return (tracker.forms || []).find((f) => f.id === currentFormId) || null;
    }
    return (
      (tracker.forms || []).find((f) => f.role === "first_submission") ||
      (tracker.forms || [])[0] ||
      null
    );
  }, [journeySteps, tracker, lateStep]);

  const allQuestions = useMemo(() => {
    if (!tracker || !form) return [];
    // A check-in gets its schedule point's extra / excluded questions.
    const current = journeySteps.find((s) => s.state === "current");
    // Late: the questions of that check-in's own date.
    const schedulePoint = lateStep
      ? lateStep.point || null
      : current?.kind === "check_in"
        ? tracker.checkInPoint || null
        : null;
    return getEffectiveFormQuestions(
      form,
      tracker.sharedQuestions || [],
      tracker.personalInfoQuestions || [],
      tracker.forms || [],
      schedulePoint
    );
  }, [tracker, form, journeySteps, lateStep]);

  const visibleQuestions = useMemo(
    () => filterMemberVisibleQuestions(allQuestions),
    [allQuestions]
  );

  const identitySeed = useMemo(
    () =>
      buildIdentitySeedAnswers(filterPublicQuestions(allQuestions), memberInfo),
    [allQuestions, memberInfo]
  );

  const { answers, setAnswer, errors, setErrors } = usePublicFormState(
    visibleQuestions,
    identitySeed
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [passwordRequired, setPasswordRequired] = useState(false);

  const title = form?.name || tracker?.name || "Check-in";
  const currentStep = useMemo(
    () => lateStep || journeySteps.find((s) => s.state === "current") || null,
    [journeySteps, lateStep]
  );

  const isEmptyValue = (value) =>
    value == null ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);

  const validateAll = () => {
    const nextErrors = {};
    visibleQuestions.forEach((q) => {
      const value = answers[q.id];
      if (q?.required && isEmptyValue(value)) nextErrors[q.id] = "Required";
      else if (
        q?.fieldType === "number" &&
        isMeasurableQuestion(q) &&
        !isEmptyValue(value) &&
        Number(value) < 0
      ) {
        nextErrors[q.id] = "Must be 0 or more";
      }
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const firstId = Object.keys(nextErrors)[0];
      document
        .getElementById(`member-q-${firstId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return false;
    }
    // Profile details are filled in for them — a check-in needs real answers.
    if (visibleQuestions.every((q) => isEmptyValue(answers[q.id]))) {
      setSubmitError("Please answer at least one question before submitting.");
      return false;
    }
    return true;
  };

  const finishSubmit = async () => {
    setSubmitError("");
    setPasswordRequired(false);
    setSubmitting(true);
    try {
      const idOrSlug = tracker.id || tracker._id || tracker.publicSlug;
      const res = await submitMemberTrackerApi(idOrSlug, {
        formId: form?.id || "",
        answers: { ...identitySeed, ...answers },
        ...(lateStep?.checkInId ? { checkInId: lateStep.checkInId } : {}),
      });
      if (res?.code === 200) {
        onSuccess?.(res);
        return;
      }
      if (isJourneyPasswordRequired(res)) {
        // Journey got locked after the page loaded — unlock it from the journey page.
        setPasswordRequired(true);
        setSubmitError(res?.message || "This journey is password protected");
        return;
      }
      if (res?.errors && typeof res.errors === "object") {
        setErrors((prev) => ({ ...prev, ...res.errors }));
      }
      setSubmitError(res?.message || "Submission failed. Please try again.");
    } catch (_e) {
      setSubmitError("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validateAll()) return;
    finishSubmit();
  };

  // Which check-in this is: the missed one sent late, or the open one.
  const target = lateStep || currentStep;
  const targetDate = target?.opensAt || target?.unlockAt || null;
  const isStart = target?.kind === "start" || form?.role === "first_submission";
  const eyebrow = [
    isStart ? form?.name || "Start form" : "Check-in",
    targetDate ? moment(targetDate).format("ddd D MMM") : "",
    (() => {
      if (!targetDate) return "";
      const days = moment().startOf("day").diff(moment(targetDate).startOf("day"), "days");
      // Past its date (open or window closed) → missed; today → due today.
      if (days > 0 && (lateStep || target?.state === "current")) return "Missed";
      if (target?.state === "current") return "Due today";
      return "";
    })(),
  ]
    .filter(Boolean)
    .join(" · ");
  const intro = targetDate
    ? `Record your results for ${moment(targetDate).format("dddd D MMM")}.${
        lateStep ? " It counts in its own week." : ""
      } Your coach will see them on your journey.`
    : "Record your results. Your coach will see them on your journey.";

  const general = useMemo(
    () => visibleQuestions.filter((q) => !isMeasurableQuestion(q)),
    [visibleQuestions]
  );
  const measures = useMemo(
    () => visibleQuestions.filter((q) => isMeasurableQuestion(q)),
    [visibleQuestions]
  );
  const answeredCount = visibleQuestions.filter(
    (q) => !isEmptyValue(answers[q.id])
  ).length;

  // "Last time": the latest earlier check-in with an answer (a late check-in
  // compares with the ones before its own date).
  const lastValues = useMemo(() => {
    const before = targetDate && lateStep ? new Date(targetDate) : new Date();
    const rows = memberSubs
      .filter((row) => new Date(row.scheduledFor || row.submittedAt || 0) < before)
      .sort(
        (a, b) =>
          new Date(b.scheduledFor || b.submittedAt || 0) -
          new Date(a.scheduledFor || a.submittedAt || 0)
      );
    const out = {};
    measures.forEach((q) => {
      const row = rows.find((r) => answerOnRow(r, q) != null);
      out[q.id] = row ? answerOnRow(row, q) : null;
    });
    return out;
  }, [memberSubs, measures, targetDate, lateStep]);
  const hasLastValues = measures.some((q) => lastValues[q.id] != null);

  if (!visibleQuestions.length) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography sx={{ fontWeight: 600, color: INK, mb: 1, fontSize: 18 }}>
          Nothing to fill in
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: 14, mb: 2 }}>
          This journey has no member questions yet.
        </Typography>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={onCancel}
        >
          Back
        </button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate sx={formSx}>
      <GrCard>
        <GrPageHeader
          sx={{ mb: 0 }}
          eyebrow={eyebrow}
          title={tracker?.name || title}
          subtitle={intro}
          actions={
            <GrBadge sx={answeredWideSx}>
              {answeredCount} of {visibleQuestions.length} answered
            </GrBadge>
          }
        />
        {/* Phones: under the text, so the title and intro keep the full width. */}
        <GrBadge sx={answeredPhoneSx}>
          {answeredCount} of {visibleQuestions.length} answered
        </GrBadge>
      </GrCard>

      <GrCard sx={bodyCardSx}>
        {general.length ? (
          <>
            <GrCardHeader
              as="h2"
              sx={{ mb: 0 }}
              title={sectionTitle("General questions", general.length)}
            />
            {general.map((question, index) => (
              <React.Fragment key={question.id}>
                {index ? <Box sx={dividerSx} /> : null}
                <GeneralQuestion
                  question={question}
                  value={answers[question.id]}
                  error={errors[question.id]}
                  onChange={(value) => setAnswer(question.id, value)}
                />
              </React.Fragment>
            ))}
          </>
        ) : null}

        {measures.length ? (
          <>
            {general.length ? <Box sx={dividerSx} /> : null}
            <GrCardHeader
              as="h2"
              sx={{ mb: 0 }}
              title={sectionTitle("Measurements", measures.length)}
              subtitle={
                hasLastValues
                  ? "Compared with your last check-in as you type."
                  : null
              }
            />
            {measures.map((question, index) => (
              <React.Fragment key={question.id}>
                {index ? <Box sx={dividerSx} /> : null}
                <MeasureQuestion
                  question={question}
                  value={answers[question.id]}
                  error={errors[question.id]}
                  onChange={(value) => setAnswer(question.id, value)}
                  lastValue={lastValues[question.id]}
                />
              </React.Fragment>
            ))}
          </>
        ) : null}

        {submitError ? (
          <GrText variant="secondary" sx={{ color: gr.color.danger }}>
            {submitError}
            {passwordRequired ? (
              <GrButton variant="link" onClick={onCancel} sx={{ ml: gr.space[2] }}>
                Back to journey to unlock it
              </GrButton>
            ) : null}
          </GrText>
        ) : null}

        <Box sx={actionsSx}>
          <GrButton variant="quiet" disabled={submitting} onClick={onCancel}>
            Cancel
          </GrButton>
          <GrButton type="submit" loading={submitting}>
            {submitting ? "Submitting…" : "Submit check-in"}
          </GrButton>
        </Box>
      </GrCard>
    </Box>
  );
}
